import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import { NAVBAR_MENU_KEYS, NS } from '../../../i18n/constants/keys';
import {
  CompassIcon,
  HeartHandshakeIcon,
  IdCardIcon,
  SettingsIcon,
  SlidersHorizontalIcon,
  UserCheckIcon,
  UserRoundPlusIcon,
  ChevronRightIcon,
} from '../../../icons/animated';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import styles from '../NavBar.module.css';

interface SettingsNavLink {
  titleKey: string;
  descriptionKey: string;
  path: string;
  assignedPermissions?: string[];
  assignedSubscription?: string;
  icon: React.ReactNode;
}

const adminSettings = {
  assignedPermissions: [
    Permission.ROLES_VIEW,
    Permission.USERS_VIEW,
    Permission.GROUPS_VIEW,
  ],
  matchAnyPermission: true,
  navLinks: [
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.usersRoles.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.usersRoles.description,
      path: '/access-management',
      assignedPermissions: [Permission.USERS_VIEW],
      icon: (
        <UserRoundPlusIcon size={20} color="#059669" />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.assessmentFramework.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.assessmentFramework.description,
      path: '/assessment-framework',
      assignedPermissions: [Permission.ASSESSMENTS_VIEW],
      icon: (
        <CompassIcon size={20} color="#059669" />
      ),
    },
    {
      titleKey:
        NAVBAR_MENU_KEYS.systemSettings.items.organizationSettings.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.organizationSettings.description,
      path: '/organization-settings',
      assignedPermissions: [Permission.ORGUNIT_VIEW],
      icon: (
        <SlidersHorizontalIcon size={20} color="#059669" />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.employeeManagement.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.employeeManagement.description,
      path: '/employee-management',
      assignedPermissions: [],
      icon: (
        <IdCardIcon size={20} color="#059669" />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.candidateManagement.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.candidateManagement.description,
      path: '/candidate-management',
      assignedPermissions: [],
      icon: (
        <UserCheckIcon size={20} color="#059669" />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.partnerManagement.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.partnerManagement.description,
      path: '/partner-management',
      assignedPermissions: [],
      icon: (
        <HeartHandshakeIcon size={20} color="#059669" />
      ),
    },
  ],
};

export const SettingsMenu: React.FC = () => {
  const { t } = useTranslation(NS.navbarMenus);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (menuOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setMenuOpen(false);
        setIsAnimating(false);
      }, 150);
    } else {
      setMenuOpen(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        if (menuOpen) {
          setIsAnimating(true);
          setTimeout(() => {
            setMenuOpen(false);
            setIsAnimating(false);
          }, 150);
        }
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) {
        setIsAnimating(true);
        setTimeout(() => {
          setMenuOpen(false);
          setIsAnimating(false);
        }, 150);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [menuOpen]);

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box sx={{ position: 'relative' }} ref={menuRef}>
      <IconButton
        onClick={handleMenuToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={styles.settingsButton}
        aria-label="Settings"
        size="small"
        sx={{ p: 0.75, borderRadius: '8px', transition: 'transform 0.2s ease' }}
      >
        <SettingsIcon isHovered={isHovered} size={20} color="var(--navbar-text, #ffffff)" />
      </IconButton>

      {menuOpen && (
        <Paper
          elevation={16}
          sx={{
            position: 'absolute',
            right: 0,
            mt: 1.5,
            width: { xs: 288, sm: 384 },
            maxWidth: 'calc(100vw - 16px)',
            bgcolor: '#ffffff',
            border: '1px solid #f1f5f9',
            borderRadius: '16px',
            overflow: 'hidden',
            backdropFilter: 'blur(8px)',
            transform: isAnimating ? 'scale(0.95) translateY(8px)' : 'scale(1) translateY(0)',
            opacity: isAnimating ? 0 : 1,
            transition: 'all 0.2s',
            zIndex: 9999,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(to right, #059669, #047857, #065f46)',
              px: 3,
              py: 2.5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to right, rgba(255, 255, 255, 0.05), transparent)',
              }}
            />
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <SettingsIcon size={20} color="#ffffff" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.25, lineHeight: 1.2 }}>
                  {t(NAVBAR_MENU_KEYS.systemSettings.title)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#d1fae5', opacity: 0.9 }}>
                  {t(NAVBAR_MENU_KEYS.systemSettings.description)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Menu Items */}
          <Box sx={{ py: 1, maxHeight: 384, overflowY: 'auto' }}>
            {adminSettings.navLinks.map((item) => {
              const isActive = location.pathname === item.path;
              const link = (
                <Box
                  component={Link}
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    setIsAnimating(true);
                    setTimeout(() => {
                      setMenuOpen(false);
                      setIsAnimating(false);
                    }, 150);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    px: 3,
                    py: 2,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    ...(isActive
                      ? {
                          bgcolor: '#ecfdf5',
                          borderRight: '4px solid #10b981',
                        }
                      : {
                          '&:hover': {
                            bgcolor: '#f9fafb',
                            borderRight: '4px solid #a7f3d0',
                            '& .menu-icon-box': { bgcolor: '#ecfdf5' },
                            '& .menu-title': { color: '#059669' },
                            '& .menu-desc': { color: '#10b981' },
                            '& .menu-arrow': {
                              color: '#34d399',
                              transform: 'translateX(4px)',
                            },
                          },
                        }),
                  }}
                >
                  {/* Icon */}
                  <Box
                    className="menu-icon-box"
                    sx={{
                      flexShrink: 0,
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      ...(isActive
                        ? {
                            bgcolor: '#d1fae5',
                            boxShadow: '0 0 0 2px #a7f3d0',
                          }
                        : {
                            bgcolor: '#f1f5f9',
                          }),
                    }}
                  >
                    {item.icon}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      className="menu-title"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        transition: 'color 0.2s',
                        color: isActive ? '#047857' : '#111827',
                      }}
                    >
                      {t(item.titleKey)}
                    </Typography>
                    <Typography
                      className="menu-desc"
                      sx={{
                        fontSize: '0.75rem',
                        mt: 0.25,
                        transition: 'color 0.2s',
                        color: isActive ? '#059669' : '#6b7280',
                      }}
                    >
                      {t(item.descriptionKey)}
                    </Typography>
                  </Box>

                  {/* Active Indicator */}
                  {isActive && (
                    <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
                      <Box sx={{ width: 8, height: 8, bgcolor: '#10b981', borderRadius: '50%' }} />
                    </Box>
                  )}

                  {/* Arrow Icon */}
                  <Box
                    className="menu-arrow"
                    sx={{
                      flexShrink: 0,
                      alignSelf: 'center',
                      transition: 'all 0.2s',
                      color: isActive ? '#10b981' : '#9ca3af',
                    }}
                  >
                    <ChevronRightIcon size={16} />
                  </Box>
                </Box>
              );

              const needsGate =
                (item.assignedPermissions?.length ?? 0) > 0 ||
                Boolean((item as any).assignedSubscription);

              return needsGate ? (
                <PermissionGate
                  key={item.path}
                  requiredPermissions={item.assignedPermissions}
                  matchAnyPermission={adminSettings.matchAnyPermission}
                  requiredFeature={(item as any).assignedSubscription}
                >
                  {link}
                </PermissionGate>
              ) : (
                link
              );
            })}
          </Box>

          {/* Footer */}
          <Box sx={{ borderTop: '1px solid #f1f5f9', px: 3, py: 1.5, bgcolor: '#f9fafb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                {t(NAVBAR_MENU_KEYS.systemSettings.footerConfiguration)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, bgcolor: '#4ade80', borderRadius: '50%' }} />
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                  {t(NAVBAR_MENU_KEYS.systemSettings.online)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SettingsMenu;
